import { supabase } from './supabaseService';
import type {
  BulkThreatExplainPayload,
  ThreatExplainPayload,
  ThreatExplainSummary,
} from './threatExplainService';

export type GenerationSource =
  | 'single'
  | 'single_fn'
  | 'bulk_home'
  | 'bulk_fn'
  | 'bulk_notes';

export type SummaryOrigin = 'ai' | 'edit';

// A summary is identified by the full tuple — threat_id alone is NOT unique
// (one domain appears once per company/asset pair).
export interface ThreatRef {
  threat_id: number;
  protected_asset_id: number;
  company_id: number;
}

// One row per version in threat_explain_summaries (append-only). The current
// summary for a threat is the latest row for its identity tuple.
export interface ThreatExplainSummaryRow extends ThreatRef {
  id: string;
  generation_id: string | null;
  text: string;
  origin: SummaryOrigin;
  user_id: string;
  user_email: string;
  prompt_version: string | null;
  created_at: string;
}

interface UserIdentity {
  user_id: string;
  user_email: string;
}

interface LogGenerationParams extends UserIdentity {
  threat_id: number;
  protected_asset_id: number | null;
  company_id: number | null;
  prompt_version: string;
  payload: ThreatExplainPayload;
  summary: ThreatExplainSummary;
}

export async function logGeneration(
  params: LogGenerationParams,
): Promise<string> {
  const { data, error } = await supabase
    .from('threat_explain_generations')
    .insert({
      threat_id: params.threat_id,
      protected_asset_id: params.protected_asset_id,
      company_id: params.company_id,
      user_id: params.user_id,
      user_email: params.user_email,
      prompt_version: params.prompt_version,
      payload_json: params.payload,
      // Keyed by model so historical 3-model rows and new single-model rows
      // stay queryable the same way.
      verdicts_json: { haiku: params.summary },
    })
    .select('id')
    .single();

  if (error) throw new Error(`logGeneration failed: ${error.message}`);
  return data.id as string;
}

interface LogBulkGenerationParams extends UserIdentity {
  source: Exclude<GenerationSource, 'single'>;
  protected_asset_id: number | null;
  company_id: number | null;
  prompt_version: string;
  payload: BulkThreatExplainPayload;
  summary: ThreatExplainSummary;
}

// Bulk generations are logged once per batch (threat_id stays null) so cost
// queries count the spend once, not once per created threat.
export async function logBulkGeneration(
  params: LogBulkGenerationParams,
): Promise<string> {
  const { data, error } = await supabase
    .from('threat_explain_generations')
    .insert({
      threat_id: null,
      source: params.source,
      domain_count: params.payload.domains.length,
      protected_asset_id: params.protected_asset_id,
      company_id: params.company_id,
      user_id: params.user_id,
      user_email: params.user_email,
      prompt_version: params.prompt_version,
      payload_json: params.payload,
      verdicts_json: { haiku: params.summary },
    })
    .select('id')
    .single();

  if (error) throw new Error(`logBulkGeneration failed: ${error.message}`);
  return data.id as string;
}

// Everything needed to persist a bulk summary once the threats exist. Held in
// modal state between generation/edit and the create-threats API response.
export interface BulkExplainSummary extends UserIdentity {
  generation_id: string;
  text: string;
  origin: SummaryOrigin;
  prompt_version: string;
}

interface InsertSummaryParams extends ThreatRef, UserIdentity {
  generation_id: string | null;
  text: string;
  origin: SummaryOrigin;
  prompt_version: string | null;
}

// Append a new version. Never updates — history is the point.
export async function insertSummary(
  params: InsertSummaryParams,
): Promise<ThreatExplainSummaryRow> {
  const { data, error } = await supabase
    .from('threat_explain_summaries')
    .insert({
      threat_id: params.threat_id,
      protected_asset_id: params.protected_asset_id,
      company_id: params.company_id,
      user_id: params.user_id,
      user_email: params.user_email,
      generation_id: params.generation_id,
      text: params.text,
      origin: params.origin,
      prompt_version: params.prompt_version,
    })
    .select('*')
    .single();

  if (error) throw new Error(`insertSummary failed: ${error.message}`);
  return data as ThreatExplainSummaryRow;
}

// Fan the batch narrative out to every created threat as its first version.
export async function insertSummaries(
  threats: ThreatRef[],
  summary: BulkExplainSummary,
): Promise<void> {
  if (threats.length === 0) return;

  const rows = threats.map(t => ({
    threat_id: t.threat_id,
    protected_asset_id: t.protected_asset_id,
    company_id: t.company_id,
    user_id: summary.user_id,
    user_email: summary.user_email,
    generation_id: summary.generation_id,
    text: summary.text,
    origin: summary.origin,
    prompt_version: summary.prompt_version,
  }));

  const { error } = await supabase
    .from('threat_explain_summaries')
    .insert(rows);

  if (error) throw new Error(`insertSummaries failed: ${error.message}`);
}

export async function getCurrentSummary(
  ref: ThreatRef,
): Promise<ThreatExplainSummaryRow | null> {
  const { data, error } = await supabase
    .from('threat_explain_summaries')
    .select('*')
    .eq('threat_id', ref.threat_id)
    .eq('protected_asset_id', ref.protected_asset_id)
    .eq('company_id', ref.company_id)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw new Error(`getCurrentSummary failed: ${error.message}`);
  return (data as ThreatExplainSummaryRow | null) ?? null;
}
